import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, email: "", error: "" });
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`).then(({ data }) => {
      setState({ loading: false, valid: true, email: data.email, error: "" });
    }).catch((err) => {
      setState({ loading: false, valid: false, email: "", error: err.response?.data?.detail || "Enllaç invàlid" });
    });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("Les contrasenyes no coincideixen");
    if (form.new_password.length < 8) return toast.error("Mínim 8 caràcters");
    setSaving(true);
    try {
      await api.post(`/auth/reset-password/${token}`, { new_password: form.new_password });
      toast.success("Contrasenya restablerta! Ara pots accedir.");
      setTimeout(() => nav("/login"), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-6 py-20">
        <Card className="rounded-3xl border-2 shadow-xl shadow-primary/5">
          <CardContent className="p-8">
            {state.loading ? (
              <div className="text-center text-muted-foreground" data-testid="reset-loading">Verificant enllaç…</div>
            ) : !state.valid ? (
              <div className="text-center" data-testid="reset-invalid">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6" aria-hidden="true" />
                </div>
                <h1 className="font-heading font-bold text-2xl">Enllaç no vàlid</h1>
                <p className="text-sm text-muted-foreground mt-2">{state.error}</p>
                <Button asChild variant="outline" className="mt-6 rounded-full" data-testid="reset-retry-btn">
                  <Link to="/oblidat">Demanar un nou enllaç</Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mb-4">
                    <KeyRound className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <h1 className="font-heading font-bold text-2xl">Nova contrasenya</h1>
                  <p className="text-sm text-muted-foreground mt-1">Per a <strong>{state.email}</strong></p>
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                  <div>
                    <Label>Nova contrasenya</Label>
                    <Input type="password" required autoFocus autoComplete="new-password" minLength={8} className="mt-2 rounded-xl" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="reset-new-input" />
                  </div>
                  <div>
                    <Label>Confirma</Label>
                    <Input type="password" required autoComplete="new-password" minLength={8} className="mt-2 rounded-xl" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="reset-confirm-input" />
                  </div>
                  <Button type="submit" disabled={saving} size="lg" className="w-full rounded-full h-12" data-testid="reset-submit-btn">
                    <KeyRound className="w-4 h-4 mr-2" aria-hidden="true" /> {saving ? "Desant…" : "Establir contrasenya"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
